import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Calendar, Loader2, MessageCircle, X, AlertCircle, Bot, UserRound } from "lucide-react";
import api from "../../shared/services/api";
import { listContas } from "../WhatsappOficial/services/whatsappOficialApi";

const PRESETS = [
  { id: "7d",  label: "7 dias",  dias: 7  },
  { id: "30d", label: "30 dias", dias: 30 },
  { id: "90d", label: "90 dias", dias: 90 },
];

function isoDaysAgo(n) {
  return new Date(Date.now() - n * 24 * 3600 * 1000).toISOString();
}
function pct(num, den) {
  if (!den || den <= 0) return 0;
  return Math.round((num / den) * 1000) / 10;
}
function fmtData(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export default function WhatsappRelatorios() {
  const [contas, setContas]     = useState([]);
  const [contaId, setContaId]   = useState(null);
  const [preset, setPreset]     = useState("30d");
  const [carregando, setCarregando] = useState(false);
  const [dados, setDados]       = useState(null);
  // Modal de drill-down de falhas (clicando no número da coluna "Falhas").
  const [modalFalhas, setModalFalhas] = useState(null); // { template, language, falhas: [...], carregando: bool }
  const [modalDetalhe, setModalDetalhe] = useState(null); // { template, language, data, carregando }

  useEffect(() => {
    listContas()
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        setContas(lista);
        const ativa = lista.find((c) => c.status === "ativo") || lista[0];
        if (ativa) setContaId(ativa.id);
      })
      .catch(() => setContas([]));
  }, []);

  useEffect(() => {
    if (!contaId) return;
    let cancelado = false;
    const preset_atual = PRESETS.find((p) => p.id === preset) || PRESETS[1];
    const desde = isoDaysAgo(preset_atual.dias);
    const ate   = new Date().toISOString();
    setCarregando(true);
    api
      .get(`/whatsapp/meta/contas/${contaId}/relatorios/templates`, { params: { desde, ate } })
      .then((r) => {
        if (cancelado) return;
        setDados(r.data?.data ?? null);
      })
      .catch(() => { if (!cancelado) setDados(null); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  }, [contaId, preset]);

  const linhasOrdenadas = useMemo(() => {
    const arr = Array.isArray(dados?.linhas) ? [...dados.linhas] : [];
    return arr.sort((a, b) => (b.aceitas || b.enviadas || 0) - (a.aceitas || a.enviadas || 0));
  }, [dados]);

  const totais = dados?.totais || { aceitas: 0, enviadas: 0, entregues: 0, lidas: 0, respondidas: 0, falhas: 0 };

  async function abrirFalhas(template, language) {
    if (!contaId) return;
    setModalFalhas({ template, language, falhas: [], carregando: true });
    try {
      const preset_atual = PRESETS.find((p) => p.id === preset) || PRESETS[1];
      const params = {
        desde: isoDaysAgo(preset_atual.dias),
        ate: new Date().toISOString(),
        template,
        language,
      };
      const r = await api.get(`/whatsapp/meta/contas/${contaId}/relatorios/templates/falhas`, { params });
      setModalFalhas({ template, language, falhas: r.data?.data?.falhas || [], carregando: false });
    } catch (_err) {
      setModalFalhas({ template, language, falhas: [], carregando: false });
    }
  }

  async function abrirDetalhe(template, language) {
    if (!contaId) return;
    setModalDetalhe({ template, language, data: null, carregando: true });
    try {
      const preset_atual = PRESETS.find((p) => p.id === preset) || PRESETS[1];
      const params = {
        desde: isoDaysAgo(preset_atual.dias),
        ate: new Date().toISOString(),
        template,
        language,
        limit: 300,
      };
      const r = await api.get(`/whatsapp/meta/contas/${contaId}/relatorios/templates/detalhes`, { params });
      setModalDetalhe({ template, language, data: r.data?.data || null, carregando: false });
    } catch (_err) {
      setModalDetalhe({ template, language, data: null, carregando: false });
    }
  }

  function fecharModal() { setModalFalhas(null); }
  function fecharDetalhe() { setModalDetalhe(null); }

  if (contas.length === 0) {
    return (
      <div className="min-h-screen bg-base flex flex-col items-center justify-center text-ink-2 p-6 sm:ml-16">
        <MessageCircle className="w-12 h-12 text-ink-5 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhuma conta WhatsApp conectada</h2>
        <p className="text-sm text-ink-3 max-w-md text-center mb-6">
          Conecte uma conta Meta WhatsApp Cloud API em{" "}
          <strong>WhatsApp → Oficial (Meta)</strong> antes de ver os relatórios.
        </p>
        <Link
          to="/whatsapp/oficial"
          className="px-4 py-2 bg-brand-600 hover:bg-brand-500 rounded text-white text-sm transition"
        >
          Conectar conta
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base sm:ml-16">
      <header className="px-6 py-4 border-b border-white/[0.06] flex items-center gap-3">
        <BarChart3 className="w-5 h-5 text-brand-400" />
        <h1 className="text-lg font-semibold text-white">Relatórios WhatsApp</h1>
        <span className="text-xs text-ink-4 ml-2">por template</span>
      </header>

      <div className="px-6 py-5 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          {contas.length > 1 && (
            <select
              value={contaId ?? ""}
              onChange={(e) => setContaId(parseInt(e.target.value, 10))}
              className="bg-surface-1 border border-white/[0.06] rounded px-3 py-1.5 text-xs text-ink-2 focus:outline-none focus:border-brand-600"
            >
              {contas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.phone_number || `Conta #${c.id}`}
                </option>
              ))}
            </select>
          )}
          <div className="inline-flex items-center gap-1 bg-surface-1 border border-white/[0.06] rounded p-1">
            <Calendar className="w-3.5 h-3.5 text-ink-5 mx-1.5" />
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPreset(p.id)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  preset === p.id
                    ? "bg-brand-600 text-white"
                    : "text-ink-3 hover:text-ink-2 hover:bg-surface-2"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {carregando && (
            <Loader2 className="w-4 h-4 animate-spin text-ink-4" aria-label="Carregando" />
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <CardTotal label="Aceitas"     valor={totais.aceitas ?? totais.enviadas} cor="brand" />
          <CardTotal label="Em sent"     valor={totais.em_sent || 0} cor="amber" />
          <CardTotal label="Entregues"   valor={totais.entregues}   cor="blue"
            sub={`${pct(totais.entregues, totais.enviadas)}%`} />
          <CardTotal label="Lidas"       valor={totais.lidas}       cor="emerald"
            sub={`${pct(totais.lidas, totais.enviadas)}%`} />
          <CardTotal label="Respondidas" valor={totais.respondidas} cor="amber"
            sub={`${pct(totais.respondidas, totais.enviadas)}%`} />
          <CardTotal label="Falhas"      valor={totais.falhas}      cor="red"
            sub={`${pct(totais.falhas, totais.enviadas)}%`} />
        </div>

        <div className="bg-surface-1 border border-white/[0.06] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface-2 text-ink-3">
                <tr>
                  <th className="text-left  px-4 py-2.5 font-medium">Template</th>
                  <th className="text-left  px-4 py-2.5 font-medium">Idioma</th>
                  <th className="text-right px-4 py-2.5 font-medium">Aceitas</th>
                  <th className="text-right px-4 py-2.5 font-medium">Entregues</th>
                  <th className="text-right px-4 py-2.5 font-medium">Lidas</th>
                  <th className="text-right px-4 py-2.5 font-medium">Respondidas</th>
                  <th className="text-right px-4 py-2.5 font-medium">Falhas</th>
                  <th className="text-right px-4 py-2.5 font-medium">Período</th>
                </tr>
              </thead>
              <tbody>
                {linhasOrdenadas.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-ink-5 italic">
                      Nenhum template enviado neste período.
                    </td>
                  </tr>
                )}
                {linhasOrdenadas.map((l) => (
                  <tr key={`${l.template_name}::${l.language}`} className="border-t border-white/[0.04] hover:bg-surface-2/50">
                    <td className="px-4 py-2.5 text-ink-1 font-medium">
                      <button
                        type="button"
                        onClick={() => abrirDetalhe(l.template_name, l.language)}
                        className="text-left text-brand-200 hover:text-brand-100 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded"
                        aria-label={`Abrir dashboard do template ${l.template_name}`}
                      >
                        {l.template_name}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-ink-3">{l.language || "—"}</td>
                    <td className="px-4 py-2.5 text-ink-1 text-right">{l.aceitas ?? l.enviadas}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="text-ink-1">{l.entregues}</span>
                      <span className="text-ink-5 ml-1.5 text-[10px]">{pct(l.entregues, l.aceitas ?? l.enviadas)}%</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="text-emerald-300">{l.lidas}</span>
                      <span className="text-ink-5 ml-1.5 text-[10px]">{pct(l.lidas, l.aceitas ?? l.enviadas)}%</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="text-amber-300">{l.respondidas}</span>
                      <span className="text-ink-5 ml-1.5 text-[10px]">{pct(l.respondidas, l.aceitas ?? l.enviadas)}%</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {l.falhas > 0 ? (
                        <button
                          type="button"
                          onClick={() => abrirFalhas(l.template_name, l.language)}
                          className="text-red-400 hover:text-red-300 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 rounded"
                          aria-label={`Ver detalhes das ${l.falhas} falhas de ${l.template_name}`}
                        >
                          {l.falhas}
                        </button>
                      ) : (
                        <span className="text-ink-5">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-ink-4 text-right whitespace-nowrap">
                      {fmtData(l.primeira)} → {fmtData(l.ultima)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {modalFalhas && <FalhasModal info={modalFalhas} onClose={fecharModal} />}
        {modalDetalhe && <TemplateDashboardModal info={modalDetalhe} onClose={fecharDetalhe} />}

        <p className="text-[10px] text-ink-5 leading-relaxed">
          <strong className="text-ink-3">Respondida</strong>: o cliente enviou alguma mensagem
          em até 24h após o template. <strong className="text-ink-3">Lidas</strong> e{" "}
          <strong className="text-ink-3">Entregues</strong> são <em>inclusivas das respostas</em> —
          se o cliente respondeu, contamos como lida e entregue mesmo sem o callback `read`/`delivered`
          do Meta (clientes com recibos de leitura desligados não geram esse callback).
          Coerência garantida: <code>aceitas ≥ entregues ≥ lidas ≥ respondidas</code>.
        </p>
      </div>
    </div>
  );
}

function fmtDataHora(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function resumoAgente(resultado, detalhes) {
  if (!resultado) return "—";
  const via = detalhes?.via === "rag" ? "RAG" : detalhes?.via;
  if (resultado === "enviado") return via ? `Enviado · ${via}` : "Enviado";
  if (resultado === "fallback") return "Fallback";
  if (resultado === "sem_match") return "Sem match";
  if (resultado === "cooldown") return "Cooldown";
  if (resultado === "erro") return "Erro";
  return resultado;
}

function TemplateDashboardModal({ info, onClose }) {
  const { template, language, data, carregando } = info;
  const totais = data?.totais || {};
  const linhas = Array.isArray(data?.linhas) ? data.linhas : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Dashboard do template ${template}`}
        className="bg-surface-1 border border-white/[0.08] rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-shrink-0">
          <BarChart3 className="w-4 h-4 text-brand-300" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-white truncate">{template}</h2>
            <p className="text-[11px] text-ink-4">{language || "sem idioma"} · dashboard de respostas e agente</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-4 hover:text-ink-2 rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-ink-4" />
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4 border-b border-white/[0.06]">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-ink-4 mb-2">Funil resumido</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <CardTotal label="Validador rejeitou" valor={totais.validador_rejeitou || 0} cor="red"
                    sub="não chegaram à Meta" />
                  <CardTotal label="Enviadas (Meta aceitou)" valor={totais.aceitas ?? totais.enviadas ?? 0} cor="brand" />
                  <CardTotal label="Meta rejeitou" valor={totais.falhas || 0} cor="red"
                    sub={`${pct(totais.falhas, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Lidas" valor={totais.lidas || 0} cor="emerald"
                    sub={`${pct(totais.lidas, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Bot respondeu" valor={totais.agente_respondeu || 0} cor="amber"
                    sub={`${pct(totais.agente_respondeu, totais.respondidas)}% das respostas`} />
                  <CardTotal label="Compraram" valor={totais.compras_imersao || 0} cor="emerald"
                    sub={`${pct(totais.compras_imersao, totais.agente_respondeu)}% dos respondidos`} />
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-ink-4 mb-2">Detalhes de entrega</p>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <CardTotal label="Em sent" valor={totais.em_sent || 0} cor="amber" />
                  <CardTotal label="Entregues" valor={totais.entregues || 0} cor="blue"
                    sub={`${pct(totais.entregues, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Respondidas" valor={totais.respondidas || 0} cor="amber"
                    sub={`${pct(totais.respondidas, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Não entregues" valor={totais.nao_entregues || 0} cor="red"
                    sub={`${pct(totais.nao_entregues, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Sem callback" valor={totais.sent_parado_1h || 0} cor="amber"
                    sub={`${pct(totais.sent_parado_1h, totais.aceitas ?? totais.enviadas)}%`} />
                  <CardTotal label="Agente proc." valor={totais.agente_processou || 0} cor="blue" />
                  <CardTotal label="RAG" valor={totais.agente_rag || 0} cor="amber" />
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-b border-white/[0.06] grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
              <MiniStat label="Enviado" valor={totais.agente_enviado || 0} />
              <MiniStat label="Fallback" valor={totais.agente_fallback || 0} />
              <MiniStat label="Sem match" valor={totais.agente_sem_match || 0} />
              <MiniStat label="Cooldown" valor={totais.agente_cooldown || 0} />
              <MiniStat label="Erro agente" valor={totais.agente_erro || 0} />
              <MiniStat label="Tag envio imersão" valor={totais.tag_envio_imersao || 0} />
              <MiniStat label="Compra + tag aprovada" valor={totais.compras_imersao_tag_aprovada || 0} />
              <MiniStat label="Sent > 6h" valor={totais.sent_parado_6h || 0} />
              <MiniStat label="Sent > 12h" valor={totais.sent_parado_12h || 0} />
            </div>

            <div className="flex-1 overflow-auto">
              {linhas.length === 0 ? (
                <div className="px-5 py-12 text-center text-ink-5 italic text-xs">
                  Nenhuma conversa encontrada nesse período.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-surface-2 text-ink-3 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Cliente</th>
                      <th className="text-left px-4 py-2 font-medium">Entrega</th>
                      <th className="text-left px-4 py-2 font-medium">Automação</th>
                      <th className="text-left px-4 py-2 font-medium">Resposta do cliente</th>
                      <th className="text-left px-4 py-2 font-medium">Agente</th>
                      <th className="text-left px-4 py-2 font-medium">Resposta do agente</th>
                      <th className="text-left px-4 py-2 font-medium">Compra</th>
                      <th className="text-right px-4 py-2 font-medium">Horários</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l) => (
                      <tr key={`${l.id}-${l.conversa_id}`} className="border-t border-white/[0.04] hover:bg-surface-2/30 align-top">
                        <td className="px-4 py-2 min-w-[160px]">
                          <div className="text-ink-1 font-medium">{l.cliente_nome || "— sem cadastro —"}</div>
                          <div className="text-ink-5 font-mono text-[10px]">{l.telefone || "—"}</div>
                        </td>
                        <td className="px-4 py-2 min-w-[115px]">
                          <EntregaBadge status={l.entrega_status_relatorio} />
                          {l.error_code && (
                            <div className="text-[10px] text-red-300 mt-1">#{l.error_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-ink-3 min-w-[140px]">
                          {l.automacao_nome || (l.automacao_id ? `#${l.automacao_id}` : "—")}
                        </td>
                        <td className="px-4 py-2 max-w-[260px]">
                          <div className="flex items-start gap-2 text-ink-2">
                            <UserRound className="w-3.5 h-3.5 mt-0.5 text-amber-300 flex-shrink-0" />
                            <span className="line-clamp-3">{l.cliente_resposta || "Sem resposta do cliente"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 min-w-[130px]">
                          <div className="flex items-center gap-1.5 text-ink-2">
                            <Bot className="w-3.5 h-3.5 text-brand-300" />
                            <span>{l.agente_nome || "—"}</span>
                          </div>
                          <div className="text-[10px] text-ink-5 mt-0.5">{resumoAgente(l.agente_resultado, l.agente_detalhes)}</div>
                        </td>
                        <td className="px-4 py-2 max-w-[280px] text-ink-2">
                          <span className="line-clamp-3">{l.agente_resposta || "—"}</span>
                        </td>
                        <td className="px-4 py-2 min-w-[150px]">
                          {l.compra_imersao_id ? (
                            <>
                              <div className="text-emerald-300 font-medium">Imersão comprada</div>
                              <div className="text-[10px] text-ink-5">
                                {l.compra_imersao_status || "—"} · {fmtDataHora(l.compra_imersao_data)}
                              </div>
                              <div className="text-[10px] text-ink-5">
                                {l.compra_imersao_tag_aprovada ? l.tag_compra_nome || "tag aprovada" : "sem tag aprovada"}
                              </div>
                            </>
                          ) : (
                            <span className="text-ink-5">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right text-ink-4 whitespace-nowrap">
                          <div>Aceite: {fmtDataHora(l.aceito_em || l.enviado_em)}</div>
                          <div>Cliente: {fmtDataHora(l.cliente_respondeu_em)}</div>
                          <div>Agente: {fmtDataHora(l.agente_respondeu_em)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-5 py-2 border-t border-white/[0.06] text-[10px] text-ink-5 flex-shrink-0">
              Mostrando até 300 envios mais recentes do template no período selecionado.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, valor }) {
  return (
    <div className="bg-surface-2 border border-white/[0.04] rounded px-3 py-2">
      <div className="text-ink-5 text-[10px] uppercase tracking-wide">{label}</div>
      <div className="text-ink-1 font-semibold tabular-nums">{valor}</div>
    </div>
  );
}

function EntregaBadge({ status }) {
  const map = {
    lido: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
    respondido: "bg-amber-500/10 text-amber-300 border-amber-500/20",
    entregue: "bg-blue-500/10 text-blue-300 border-blue-500/20",
    sent_parado: "bg-amber-500/10 text-amber-300 border-amber-500/20",
    falhou: "bg-red-500/10 text-red-300 border-red-500/20",
    enviado: "bg-white/[0.04] text-ink-4 border-white/[0.06]",
    aceito: "bg-white/[0.04] text-ink-4 border-white/[0.06]",
  };
  const label = {
    lido: "Lido",
    respondido: "Respondido",
    entregue: "Entregue",
    sent_parado: "Sem callback",
    falhou: "Falhou",
    enviado: "Enviado",
    aceito: "Aceito",
  }[status] || "—";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-medium ${map[status] || map.enviado}`}>
      {label}
    </span>
  );
}

// Mensagens de erro do Meta WhatsApp Cloud API humanizadas. Lista parcial
// dos códigos comuns — fallback usa a `message` cru do payload.
const ERROS_META = {
  131026: "Não foi possível entregar — número não tem WhatsApp ativo",
  131047: "Re-engajamento necessário (fora da janela de 24h sem template)",
  131051: "Tipo de mensagem não suportado",
  131052: "Falha ao baixar mídia do remetente",
  131053: "Falha ao enviar mídia",
  131056: "Mesma conversa já existe — rate limit por destinatário",
  131057: "Template inativo",
  132000: "Variáveis do template não bateram com o esperado",
  132001: "Template não encontrado",
  132005: "Componentes do template incompatíveis (foi rejeitado/atualizado)",
  132007: "Idioma do template não disponível",
  132012: "Parâmetro de template formato inválido",
  133010: "Telefone não está registrado no WhatsApp Business",
  368: "Conta temporariamente bloqueada por violação de políticas",
};

function FalhasModal({ info, onClose }) {
  const { template, language, falhas, carregando } = info;
  // Agrupa por error_code pra dar visão rápida de onde está a maior parte.
  const grupos = useMemo(() => {
    const map = new Map();
    for (const f of falhas) {
      const k = f.error_code || 0;
      if (!map.has(k)) map.set(k, { code: k, sample_message: f.error_message, qtd: 0 });
      map.get(k).qtd += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.qtd - a.qtd);
  }, [falhas]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Falhas do template ${template}`}
        className="bg-surface-1 border border-white/[0.08] rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-white truncate">Falhas — {template}</h2>
            <p className="text-[11px] text-ink-4">{language} · {falhas.length} mensagens com erro</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-4 hover:text-ink-2 rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            aria-label="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {grupos.length > 0 && (
          <div className="px-5 py-3 border-b border-white/[0.06] bg-surface-2/50 flex-shrink-0">
            <p className="text-[10px] uppercase tracking-wider text-ink-4 mb-2">Resumo por motivo</p>
            <div className="space-y-1">
              {grupos.map((g) => (
                <div key={g.code} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-red-300 w-12 flex-shrink-0">#{g.code || "—"}</span>
                  <span className="text-ink-2 flex-1 truncate">
                    {ERROS_META[g.code] || g.sample_message || "Erro desconhecido"}
                  </span>
                  <span className="text-ink-4 tabular-nums">{g.qtd}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {carregando ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-ink-4" />
            </div>
          ) : falhas.length === 0 ? (
            <div className="px-5 py-12 text-center text-ink-5 italic text-xs">
              Nenhuma falha encontrada nesse período.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-surface-2 text-ink-3 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Cliente</th>
                  <th className="text-left px-4 py-2 font-medium">Telefone</th>
                  <th className="text-left px-4 py-2 font-medium">Erro</th>
                  <th className="text-right px-4 py-2 font-medium">Quando</th>
                </tr>
              </thead>
              <tbody>
                {falhas.map((f) => (
                  <tr key={f.id} className="border-t border-white/[0.04] hover:bg-surface-2/30">
                    <td className="px-4 py-2 text-ink-1">
                      {f.cliente_nome || <span className="text-ink-5 italic">— sem cadastro —</span>}
                    </td>
                    <td className="px-4 py-2 text-ink-3 font-mono">{f.telefone || "—"}</td>
                    <td className="px-4 py-2">
                      <div className="text-red-300">
                        <span className="font-mono text-[10px] mr-1.5">#{f.error_code || "—"}</span>
                        {ERROS_META[f.error_code] || f.error_message || "Erro desconhecido"}
                      </div>
                      {f.error_message && !ERROS_META[f.error_code] && (
                        <div className="text-[10px] text-ink-5 mt-0.5 truncate">{f.error_message}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-ink-4 text-right whitespace-nowrap">
                      {f.falhou_em
                        ? new Date(f.falhou_em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-2 border-t border-white/[0.06] text-[10px] text-ink-5 flex-shrink-0">
          Limitado às 200 falhas mais recentes do período selecionado.
        </div>
      </div>
    </div>
  );
}

function CardTotal({ label, valor, sub, cor }) {
  const corClasses = {
    brand:   "text-brand-300",
    blue:    "text-blue-300",
    emerald: "text-emerald-300",
    amber:   "text-amber-300",
    red:     "text-red-400",
  }[cor] || "text-ink-1";

  return (
    <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-ink-4">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${corClasses}`}>{(valor ?? 0).toLocaleString("pt-BR")}</div>
      {sub && <div className="text-[10px] text-ink-5 mt-0.5">{sub}</div>}
    </div>
  );
}
